import { MakeLogicType, actions, connect, kea, listeners, path, reducers } from 'kea'
import { loaders } from 'kea-loaders'
import { router } from 'kea-router'

import api from 'lib/api'
import { TriggerExportProps, downloadBlob, downloadExportedAsset } from 'lib/components/ExportButton/exporter'
import { isLongRunningExportFormat } from 'lib/components/ExportButton/exportStatus'
import { lemonToast } from 'lib/lemon-ui/LemonToast'
import { delay } from 'lib/utils/async'
import { uuid } from 'lib/utils/dom'
import { eventUsageLogic } from 'lib/utils/eventUsageLogic'
import type { SessionRecordingPlayerMode } from 'scenes/session-recordings/player/sessionRecordingPlayerLogic'
import { urls } from 'scenes/urls'

import { sidePanelStateLogic } from '~/layout/navigation-3000/sidepanel/sidePanelStateLogic'
import { cohortsModel } from '~/models/cohortsModel'
import { AnyDataNode } from '~/queries/schema/schema-general'
import {
    APIErrorType,
    CohortType,
    ExportContext,
    ExportedAssetType,
    ExporterFormat,
    LocalExportContext,
    SidePanelTab,
} from '~/types'

const POLL_DELAY_MS = 10000
// Long-running formats (e.g. MP4 session-replay renders) can take 30+ minutes,
// so polling every 10s produces unhelpful timeout noise. Back off when the
// pending queue is dominated by long-running formats.
const LONG_RUNNING_POLL_DELAY_MS = 30000

// An export is still rendering while it has neither produced content nor failed.
const isRendering = (asset: ExportedAssetType): boolean => !asset.has_content && !asset.exception

const fetchExportOrNull = async (id: number): Promise<ExportedAssetType | null> => {
    try {
        return await api.exports.get(id)
    } catch {
        return null
    }
}

export const pickPollDelayMs = (pendingAssets: ExportedAssetType[]): number => {
    const pending = pendingAssets.filter(isRendering)
    if (pending.length === 0) {
        return POLL_DELAY_MS
    }
    return pending.every((asset) => isLongRunningExportFormat(asset.export_format))
        ? LONG_RUNNING_POLL_DELAY_MS
        : POLL_DELAY_MS
}

const isLocalExport = (context: ExportContext | undefined): context is LocalExportContext =>
    !!(context && 'localData' in context)

// Thrown when a synchronous export finished but the create request outlived the click's user
// activation, so the download must wait for a fresh gesture (a Download button) rather than
// auto-download — Safari silently drops programmatic downloads once activation has expired.
class ExportAwaitingDownload extends Error {
    constructor(public readonly asset: ExportedAssetType) {
        super('Export awaiting user download')
    }
}

// The user gesture that started the export is still live, so an auto-download will fire. Falls back
// to true where navigator.userActivation is unavailable (older Firefox), which tolerates a delayed
// click anyway.
const isUserActivationLive = (): boolean => navigator.userActivation?.isActive ?? true

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface exportsLogicValues {
    assetFormat: ExporterFormat | null
    exports: ExportedAssetType[]
    exportsLoading: boolean
    freshUndownloadedExports: ExportedAssetType[]
    hasReachedExportFullVideoLimit: boolean
    pollingExports: ExportedAssetType[]
    pollingExportsLoading: boolean
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface exportsLogicActions {
    openSidePanel: (
        tab: SidePanelTab,
        options?: string | undefined
    ) => {
        options: string | undefined
        tab: SidePanelTab
    } // sidePanelStateLogic
    addFresh: (exportedAsset: ExportedAssetType) => {
        exportedAsset: ExportedAssetType
    }
    createExport: ({ exportData }: any) => any
    createExportFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    createExportSuccess: (
        pollingExports: any[],
        payload?: any
    ) => {
        pollingExports: any[]
        payload?: any
    }
    createStaticCohort: (
        name: string,
        query: AnyDataNode
    ) => {
        name: string
        query: AnyDataNode
    }
    downloadExport: (exportedAsset: ExportedAssetType) => {
        exportedAsset: ExportedAssetType
    }
    loadExports: () => {
        value: true
    }
    loadExportsFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadExportsSuccess: (
        exports: ExportedAssetType[],
        payload?: {
            value: true
        }
    ) => {
        exports: ExportedAssetType[]
        payload?: {
            value: true
        }
    }
    removeFresh: (exportedAsset: ExportedAssetType) => {
        exportedAsset: ExportedAssetType
    }
    setAssetFormat: (format: ExporterFormat | null) => {
        format: ExporterFormat | null
    }
    setHasReachedExportFullVideoLimit: (hasReached: boolean) => {
        hasReached: boolean
    }
    startExport: (exportData: TriggerExportProps) => {
        exportData: TriggerExportProps
    }
    startHeatmapExport: (export_context: ExportContext) => {
        export_context: ExportContext
    }
    startReplayExport: (
        sessionRecordingId: string,
        format?: ExporterFormat,
        timestamp?: number,
        duration?: number,
        mode?: SessionRecordingPlayerMode,
        options?: {
            css_selector?: string
            filename?: string
            height?: number
            skip_inactivity?: boolean
            width?: number
        }
    ) => {
        duration: number | undefined
        format: ExporterFormat | undefined
        mode: SessionRecordingPlayerMode | undefined
        options:
            | {
                  css_selector?: string | undefined
                  filename?: string | undefined
                  height?: number | undefined
                  skip_inactivity?: boolean | undefined
                  width?: number | undefined
              }
            | undefined
        sessionRecordingId: string
        timestamp: number | undefined
    }
}

export type exportsLogicType = MakeLogicType<exportsLogicValues, exportsLogicActions>

export const exportsLogic = kea<exportsLogicType>([
    path(['scenes', 'navigation', 'sidepanel', 'exportsLogic']),
    connect(() => ({
        actions: [sidePanelStateLogic, ['openSidePanel']],
    })),

    actions({
        loadExports: true,
        startExport: (exportData: TriggerExportProps) => ({ exportData }),
        addFresh: (exportedAsset: ExportedAssetType) => ({ exportedAsset }),
        removeFresh: (exportedAsset: ExportedAssetType) => ({ exportedAsset }),
        downloadExport: (exportedAsset: ExportedAssetType) => ({ exportedAsset }),
        createStaticCohort: (name: string, query: AnyDataNode) => ({ query, name }),
        setAssetFormat: (format: ExporterFormat | null) => ({ format }),
        setHasReachedExportFullVideoLimit: (hasReached: boolean) => ({ hasReached }),
        startReplayExport: (
            sessionRecordingId: string,
            format?: ExporterFormat,
            timestamp?: number,
            duration?: number,
            mode?: SessionRecordingPlayerMode,
            options?: {
                width?: number
                height?: number
                css_selector?: string
                filename?: string
                skip_inactivity?: boolean
            }
        ) => ({ sessionRecordingId, format, timestamp, duration, mode, options }),
        startHeatmapExport: (export_context: ExportContext) => ({ export_context }),
    }),

    reducers({
        exports: [
            [] as ExportedAssetType[],
            {
                loadExportsSuccess: (_, { exports }) => exports,
            },
        ],
        assetFormat: [
            null as ExporterFormat | null,
            {
                setAssetFormat: (_, { format }) => format,
            },
        ],
        freshUndownloadedExports: [
            [] as ExportedAssetType[],
            {
                addFresh: (state, { exportedAsset }) =>
                    state.some((asset) => asset.id === exportedAsset.id) ? state : [...state, exportedAsset],
                removeFresh: (state, { exportedAsset }) => state.filter((asset) => asset.id !== exportedAsset.id),
            },
        ],
        hasReachedExportFullVideoLimit: [
            false,
            {
                setHasReachedExportFullVideoLimit: (_, { hasReached }) => hasReached,
            },
        ],
    }),

    listeners(({ actions, values, cache }) => ({
        startExport: async ({ exportData }) => {
            // Fires for every dashboard export entry point (menu bar, dropdown, export button)
            // regardless of edit permission. Format is a property so PNG is filterable.
            if (exportData.dashboard && exportData.export_format) {
                eventUsageLogic.actions.reportDashboardExported(exportData.dashboard, exportData.export_format)
            }

            if (isLocalExport(exportData.export_context)) {
                try {
                    const blob = new Blob([exportData.export_context.localData], {
                        type: exportData.export_context.mediaType,
                    })
                    downloadBlob(blob, exportData.export_context.filename)
                    lemonToast.success('Export complete!')
                } catch (e: any) {
                    lemonToast.error(`Export failed with error: ${e.message}`)
                }
                return
            }

            actions.createExport({ exportData })
        },
        createExportSuccess: () => {
            actions.loadExports()
        },
        downloadExport: ({ exportedAsset }) => {
            // Content already exists for this asset, so trigger the download synchronously — the click
            // has to run inside the user gesture or Safari silently drops it. Drop the "not downloaded"
            // highlight now that the download has been triggered.
            downloadExportedAsset(exportedAsset)
            actions.removeFresh(exportedAsset)
            lemonToast.success('Download started')
        },
        loadExportsSuccess: async ({ exports: exportsList }, breakpoint) => {
            // Surface async exports we kicked off that have now finished: the render completes long
            // after the kickoff toast, so this poll is the only completion signal the user gets.
            cache.notifiedExportIds ??= new Set<number>()
            const pending = exportsList.filter(isRendering)
            for (const fresh of values.freshUndownloadedExports) {
                // The list can be format-filtered (assetFormat), so fetch directly if it's not in it.
                const listed = exportsList.find((asset) => asset.id === fresh.id)
                const latest = listed ?? (await fetchExportOrNull(fresh.id))
                if (!latest || isRendering(latest)) {
                    // Still rendering, or a transient fetch miss: keep an out-of-list export polling.
                    if (!listed) {
                        pending.push(latest ?? fresh)
                    }
                    continue
                }
                if (cache.notifiedExportIds.has(fresh.id)) {
                    continue
                }
                if (latest.has_content) {
                    cache.notifiedExportIds.add(fresh.id)
                    const finished = latest
                    lemonToast.success('Export complete!', {
                        button: { label: 'Download', action: () => actions.downloadExport(finished) },
                    })
                } else {
                    actions.removeFresh(fresh)
                    lemonToast.error('Export failed: ' + latest.exception)
                }
            }

            if (pending.length) {
                await breakpoint(pickPollDelayMs(pending))
                actions.loadExports()
            }
        },
        createStaticCohort: async ({ query, name }) => {
            const toastId = 'toast-' + Math.random()
            try {
                lemonToast.info('Saving cohort...', { toastId, autoClose: false })
                const cohort: CohortType = await api.create('api/cohort', {
                    is_static: true,
                    name: name || 'Query cohort',
                    query: query,
                })
                cohortsModel.actions.cohortCreated(cohort)
                await delay(500) // just in case the toast is too fast
                lemonToast.dismiss(toastId)
                lemonToast.success('Cohort saved', {
                    toastId: `${toastId}-success`,
                    button: {
                        label: 'View cohort',
                        action: () => router.actions.push(urls.cohort(cohort.id)),
                    },
                })
            } catch {
                lemonToast.dismiss(toastId)
                lemonToast.error('Cohort save failed')
            }
        },
        setAssetFormat: () => {
            actions.loadExports()
        },
        startReplayExport: async ({
            sessionRecordingId,
            format = ExporterFormat.PNG,
            timestamp,
            duration = 5,
            options,
        }) => {
            const exportData: TriggerExportProps = {
                export_format: format,
                export_context: {
                    session_recording_id: sessionRecordingId,
                    timestamp: timestamp,
                    width: options?.width || 1400,
                    height: options?.height || 600,
                    filename: options?.filename || `replay-${sessionRecordingId}${timestamp ? `-t${timestamp}` : ''}`,
                    duration: duration,
                    skip_inactivity: options?.skip_inactivity ?? true,
                },
            }

            actions.startExport(exportData)
        },
        startHeatmapExport: async ({ export_context }) => {
            const exportData: TriggerExportProps = {
                export_format: ExporterFormat.PNG,
                export_context: export_context,
            }

            actions.startExport(exportData)
        },
    })),

    loaders(({ values, actions }) => ({
        exports: [
            [] as ExportedAssetType[],
            {
                loadExports: async (_, breakpoint) => {
                    await breakpoint(100)
                    const params: Record<string, any> = {}

                    // Add format filter if set
                    const format = values.assetFormat
                    if (format) {
                        params.export_format = format
                    }

                    const response = await api.exports.list(undefined, params)
                    breakpoint()

                    return response.results
                },
            },
        ],
        pollingExports: [
            [] as ExportedAssetType[],
            {
                createExport: ({ exportData }) => {
                    // Non-video exports (CSV/XLSX/PNG) run synchronously on the backend, so this
                    // request can block for a while. lemonToast.promise shows a spinner immediately
                    // and swaps to the success/failure message when it settles, so the user always
                    // gets feedback instead of a menu that looks like it did nothing.
                    const runExport = async (): Promise<string> => {
                        let response: ExportedAssetType
                        try {
                            response = await api.exports.create({
                                export_format: exportData.export_format,
                                dashboard: exportData.dashboard,
                                insight: exportData.insight,
                                export_context: exportData.export_context,
                            })
                        } catch (error) {
                            // Preserve the export-limit error so the caller can show the upsell;
                            // give everything else a friendly message for the failure toast.
                            if ((error as { data?: APIErrorType })?.data?.attr === 'export_limit_exceeded') {
                                throw error
                            }
                            const message = error instanceof Error ? error.message : String(error)
                            throw new Error('Export failed: ' + message)
                        }

                        const currentExports = values.exports
                        const updatedExports = [response, ...currentExports.filter((e) => e.id !== response.id)]
                        actions.loadExportsSuccess(updatedExports)

                        if (response.has_content) {
                            // Blocking export already finished in the request. Auto-download while the
                            // click's activation is still live; if the create call outlived it (a slow
                            // synchronous render), Safari would drop the download, so hand off to a
                            // Download button whose click is a fresh gesture.
                            if (isUserActivationLive()) {
                                downloadExportedAsset(response)
                                return 'Export complete!'
                            }
                            actions.addFresh(response)
                            throw new ExportAwaitingDownload(response)
                        }
                        if (response.exception) {
                            throw new Error('Export failed: ' + response.exception)
                        }
                        // Async export (e.g. video render) is a background job: acknowledge the
                        // kickoff and open the exports panel, where it lands once the render finishes.
                        actions.addFresh(response)
                        actions.openSidePanel(SidePanelTab.Exports)
                        return 'Export started'
                    }

                    const exportToastId = 'export-' + uuid()
                    void (async () => {
                        try {
                            await lemonToast.promise(
                                runExport(),
                                {
                                    pending: 'Preparing export…',
                                    success: 'Export complete!',
                                    error: 'Export failed',
                                },
                                {
                                    toastId: exportToastId,
                                    // Video renders finish minutes later in the exports panel, so the
                                    // kickoff toast must point somewhere instead of dead-ending.
                                    ...(isLongRunningExportFormat(exportData.export_format)
                                        ? {
                                              button: {
                                                  label: 'View exports',
                                                  action: () => actions.openSidePanel(SidePanelTab.Exports),
                                              },
                                          }
                                        : {}),
                                }
                            )
                        } catch (error) {
                            if (error instanceof ExportAwaitingDownload) {
                                // Content is ready but the auto-download would have been dropped —
                                // replace the spinner with a Download button the user can click.
                                lemonToast.dismiss(exportToastId)
                                lemonToast.success('Export complete!', {
                                    button: {
                                        label: 'Download',
                                        action: () => actions.downloadExport(error.asset),
                                    },
                                })
                                return
                            }
                            const apiError = error as { data?: APIErrorType }
                            // Show a survey when the user reaches the export limit, replacing the
                            // generic failure toast with the upsell.
                            if (apiError?.data?.attr === 'export_limit_exceeded') {
                                lemonToast.dismiss(exportToastId)
                                actions.setHasReachedExportFullVideoLimit(true)
                                lemonToast.error(apiError?.data?.detail || 'You reached your export limit.', {
                                    autoClose: false,
                                    button: {
                                        label: 'I want more',
                                        className: 'replay-export-limit-reached-button',
                                        action: () => {}, //we trigger the survey by clicking the button, but we need to keep the action for the toast to show
                                        dataAttr: 'export-limit-reached-button',
                                    },
                                })
                            }
                        }
                    })()

                    return [exportData]
                },
            },
        ],
    })),
])
